  // Keep decoded cart images connected while cart data and prices refresh.
  // This is shared with the customiser's fallback drawer renderer.
  (() => {
    const pendingImages = new WeakMap();
    const identity = (item = {}) => {
      const variantId = item.variant_id ?? item.variantId ?? item.id;
      if (variantId === null || variantId === undefined || variantId === '') return '';
      const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id || item.selling_plan_id || null;
      return JSON.stringify([String(variantId), Object.entries(item.properties || {}).sort(),
        sellingPlanId === null ? null : String(sellingPlanId)]);
    };
    const syncAttributes = (current, desired) => {
      for (const attribute of [...current.attributes]) {
        if (!desired.hasAttribute(attribute.name)) current.removeAttribute(attribute.name);
      }
      for (const attribute of desired.attributes) {
        if (current.getAttribute(attribute.name) !== attribute.value) current.setAttribute(attribute.name, attribute.value);
      }
    };
    const imageSource = image => {
      let source = image.getAttribute('src') || '';
      try { source = new URL(source, document.baseURI).href; } catch (_) { /* Keep malformed URLs inert. */ }
      return JSON.stringify([source, image.getAttribute('srcset') || '', image.getAttribute('sizes') || '']);
    };
    const syncImageMetadata = (current, desired) => {
      for (const attribute of [...current.attributes]) {
        if (!['src', 'srcset', 'sizes'].includes(attribute.name) && !desired.hasAttribute(attribute.name)) current.removeAttribute(attribute.name);
      }
      for (const attribute of desired.attributes) {
        if (!['src', 'srcset', 'sizes'].includes(attribute.name) && current.getAttribute(attribute.name) !== attribute.value) current.setAttribute(attribute.name, attribute.value);
      }
    };
    const syncImage = (current, desired) => {
      const source = imageSource(desired);
      const pending = pendingImages.get(current);
      if (source === imageSource(current)) {
        if (pending) {
          pending.image.onload = pending.image.onerror = null;
          pendingImages.delete(current);
        }
        // Do not assign src again, even when an equivalent absolute/relative
        // URL arrived in the fresh cart response.
        syncImageMetadata(current, desired);
        return;
      }
      if (pending?.source === source) {
        pending.desired = desired;
        return;
      }
      if (pending) pending.image.onload = pending.image.onerror = null;
      const image = new Image();
      const replacement = { image, source, desired };
      pendingImages.set(current, replacement);
      image.onload = async () => {
        if (typeof image.decode === 'function') await image.decode().catch(() => {});
        if (pendingImages.get(current) !== replacement || !current.isConnected) return;
        // A later render may have changed only its alt text or priority.
        image.onload = image.onerror = null;
        syncImageMetadata(image, replacement.desired);
        pendingImages.delete(current);
        current.replaceWith(image);
      };
      image.onerror = () => {
        if (pendingImages.get(current) === replacement) pendingImages.delete(current);
      };
      image.loading = 'eager';
      image.decoding = 'async';
      for (const attribute of desired.attributes) {
        if (attribute.name !== 'src' && attribute.name !== 'loading') image.setAttribute(attribute.name, attribute.value);
      }
      image.src = desired.getAttribute('src') || '';
    };
    const patchNode = (current, desired) => {
      if (current.nodeType !== desired.nodeType || current.nodeName !== desired.nodeName) {
        current.replaceWith(desired);
        return;
      }
      if (current.nodeType !== Node.ELEMENT_NODE) {
        if (current.nodeValue !== desired.nodeValue) current.nodeValue = desired.nodeValue;
        return;
      }
      if (current instanceof HTMLImageElement && desired instanceof HTMLImageElement) {
        syncImage(current, desired);
        return;
      }
      syncAttributes(current, desired);
      const oldChildren = [...current.childNodes];
      const newChildren = [...desired.childNodes];
      newChildren.forEach((child, index) => {
        if (oldChildren[index]) patchNode(oldChildren[index], child);
        else current.append(child);
      });
      oldChildren.slice(newChildren.length).forEach(child => child.remove());
    };
    const render = (body, html) => {
      const template = document.createElement('template');
      template.innerHTML = html;
      const nextList = template.content.querySelector('.cart-drawer-items');
      const currentList = body.querySelector('.cart-drawer-items');
      if (!nextList || !currentList) {
        body.replaceChildren(template.content);
        return;
      }
      const currentRows = [...currentList.children];
      const nextRows = [...nextList.children];
      const used = new Set();
      const matches = new Map();
      const lineKey = row => row.getAttribute('data-line-key') || '';
      const lineIdentity = row => row.getAttribute('data-cart-line-identity') || '';
      // Reserve exact line keys first; an identity fallback must never steal
      // another real line when discounts split an ordinary product into rows.
      nextRows.forEach(next => {
        const key = lineKey(next);
        const match = key && currentRows.find(row => !used.has(row) && lineKey(row) === key
          && (!lineIdentity(row) || !lineIdentity(next) || lineIdentity(row) === lineIdentity(next)));
        if (match) { matches.set(next, match); used.add(match); }
      });
      nextRows.forEach(next => {
        if (matches.has(next)) return;
        const value = lineIdentity(next);
        if (!value || nextRows.filter(row => lineIdentity(row) === value).length !== 1) return;
        const candidates = currentRows.filter(row => !used.has(row) && lineIdentity(row) === value);
        if (candidates.length === 1) { matches.set(next, candidates[0]); used.add(candidates[0]); }
      });
      nextRows.forEach((next, index) => {
        const row = matches.get(next) || next;
        if (row !== next) patchNode(row, next);
        const currentAtIndex = currentList.children[index];
        if (currentAtIndex !== row) currentList.insertBefore(row, currentAtIndex || null);
      });
      currentRows.filter(row => !used.has(row)).forEach(row => row.remove());
      [...body.childNodes].filter(node => node !== currentList).forEach(node => node.remove());
    };
    // Reveal only the row belonging to an explicit add. Keep scrolling local
    // to the drawer; refreshes and confirmed replacements never call this.
    const reveal = ({ drawer, body, identity: targetIdentity, bundleId } = {}) => {
      if (!drawer?.isConnected || !body?.isConnected || !drawer.contains(body)
        || !drawer.classList.contains('is-open') || drawer.classList.contains('is-closing')) return false;
      const rows = [...body.querySelectorAll('[data-cart-item]')];
      const matches = bundleId
        ? rows.filter(row => row.dataset.boardBundleId === bundleId && row.dataset.boardBundleComponent === 'deck')
        : targetIdentity ? rows.filter(row => row.dataset.cartLineIdentity === targetIdentity) : [];
      if (matches.length !== 1) return false;
      const bounds = matches[0].getBoundingClientRect();
      const viewportTop = body.getBoundingClientRect().top + body.clientTop;
      if (body.clientHeight <= 0 || bounds.height <= 0 || bounds.width <= 0) return false;
      if (!bundleId && bounds.top >= viewportTop + 8 && bounds.bottom <= viewportTop + body.clientHeight - 8) return false;
      const inset = bundleId ? 8 : Math.max(8, (body.clientHeight - bounds.height) / 2);
      const top = Math.min(Math.max(0, body.scrollTop + bounds.top - viewportTop - inset),
        Math.max(0, body.scrollHeight - body.clientHeight));
      if (Math.abs(top - body.scrollTop) < 1) return false;
      body.scrollTo({ top, left: body.scrollLeft,
        behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      return true;
    };
    window.HalfpipedCartDrawerDom = { identity, render, reveal };
  })();
